import * as React from "react";
import { Map } from "immutable";
import Popover, { PopoverAnimationVertical } from "material-ui/Popover";
import { ListItem } from "material-ui/List";
import { theme } from "styles/theme";
import { List } from "react-virtualized/dist/commonjs/List";
import { CellMeasurer, CellMeasurerCache } from "react-virtualized/dist/commonjs/CellMeasurer";
import debounce from "lodash/debounce";
import { SearchBrands } from "../product-collection-side-panel/product-collection-search-brands";
import { get } from "lodash";
import { ISpinnerStyles, Spinner, SpinnerSize } from "@nuorder/components";
import { formatMessage } from "lib/globalize-util";
import { IBrandsDataStatePayload } from "reducers/brands-data-reducers";

interface IProps {
    entities: IBrandsDataStatePayload["brandsDataById"];
    ids: string[];
    anchorEl: any;
    open: boolean;
    onRequestClose: () => void;
    onBrandChange: (brandId) => void;
    selectedBrandId?: string;
    popOverStyle?: any;
    assortmentOnly?: boolean;
    isFetchingBrands: boolean;
}

export const BrandDropdown: React.FunctionComponent<IProps> = ({
    entities,
    open,
    ids,
    anchorEl,
    onRequestClose,
    onBrandChange,
    selectedBrandId,
    popOverStyle,
    assortmentOnly,
    isFetchingBrands,
}) => {
    const cache = React.useMemo(
        () =>
            new CellMeasurerCache({
                defaultHeight: 48,
                fixedWidth: true,
                minHeight: 48,
            }),
        []
    );

    const muiStyles = React.useMemo(
        () => ({
            list: {
                maxHeight: "300px",
                maxWidth: "368px",
            },
            popover: {
                maxWidth: "400px",
                padding: "12px",
                overflowY: "auto",
            },
            selectedListItem: {
                color: theme.palette.primary1Color,
            },
            disabledListItem: {
                color: theme.palette.lightGrey,
                cursor: "not-allowed",
            },
        }),
        []
    );
    const [filters, setFilters] = React.useState(() => Map());

    const handleItemClick = React.useCallback(
        (e) => {
            setFilters((currentFilters) => currentFilters.delete("__search"));
            const brandId = e.currentTarget.getAttribute("data-brand-id");
            onBrandChange(brandId);
        },
        [onBrandChange]
    );

    // @ts-ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleSearchChange = React.useCallback(
        debounce((_filters: React.SetStateAction<Map<unknown, unknown>>) => {
            setFilters(_filters);
        }, 500),
        []
    );

    const spinnerStyle = React.useMemo<Partial<ISpinnerStyles>>(
        () => ({
            root: {
                position: "relative",
                height: "100%",
                width: "100%",
                zIndex: 999,
            },
        }),
        []
    );

    const filteredBrandIds = React.useMemo(() => {
        const searchTerm = filters.get("__search") || "";
        return ids.filter((id) =>
            ((entities[id] && entities[id].name) || "").toLowerCase().includes(searchTerm.toString().toLowerCase())
        );
    }, [ids, entities, filters]);

    const getStyle = (isBrandSelected: boolean, disabled: boolean) => {
        if (isBrandSelected) {
            return muiStyles.selectedListItem;
        } else if (disabled) {
            return muiStyles.disabledListItem;
        } else {
            return {};
        }
    };

    const renderRow = React.useCallback(
        ({ key, index, parent, style }) => {
            const brandId = filteredBrandIds[index];
            const brand = entities[brandId];
            const isBrandSelected = selectedBrandId === brandId;
            const disabled = assortmentOnly ? !get(brand, "modules.assortments.enabled") : false;
            const styles = getStyle(isBrandSelected, disabled);
            return (
                <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
                    <div style={style}>
                        <ListItem
                            data-test={`brand-${brandId}`}
                            data-brand-id={brandId}
                            key={brandId}
                            onClick={handleItemClick}
                            primaryText={brand.name}
                            disabled={disabled}
                            style={styles}
                        />
                    </div>
                </CellMeasurer>
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [entities, filteredBrandIds, assortmentOnly]
    );

    let popoverAnchorEl = null;

    if (anchorEl) {
        popoverAnchorEl = anchorEl.current ? anchorEl.current : anchorEl;
    }

    return (
        <Popover
            anchorEl={popoverAnchorEl}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
            animation={PopoverAnimationVertical}
            canAutoPosition={true}
            onRequestClose={onRequestClose}
            open={open}
            style={popOverStyle ? popOverStyle : (muiStyles.popover as any)}
            targetOrigin={{ horizontal: "left", vertical: "top" }}
        >
            <SearchBrands className="searchBands" onChange={handleSearchChange} filters={filters} resource="brand" />
            <List
                deferredMeasurementCache={cache}
                height={300}
                rowCount={filteredBrandIds.length}
                rowHeight={cache.rowHeight}
                rowRenderer={renderRow}
                style={muiStyles.list}
                width={400}
            />
            {isFetchingBrands && (
                <Spinner label={formatMessage("Loading...")} size={SpinnerSize.large} styles={spinnerStyle} />
            )}
        </Popover>
    );
};
